/**
 * Observabilidade do envio de atestado pelo funcionário (atestado.zavy.com.br; em dev, /enviar/<slug>).
 * A página trata os erros na tela (o funcionário vê a mensagem) — sem isto nada chegava ao Sentry.
 * Nunca manda CPF, data de nascimento ou e-mail: só a empresa do endereço, o passo e a operação.
 */
import * as Sentry from '@sentry/react'
import { ehHostDoEnvio } from './endereco-envio'

export type OperacaoEnvio = 'canal' | 'identificar' | 'verificar_arquivo' | 'enviar' | 'ler_arquivo' | 'turnstile'

export function ehPaginaDoEnvio(loc: { hostname: string; pathname: string }): boolean {
  return ehHostDoEnvio(loc.hostname) || loc.pathname.startsWith('/enviar/')
}

export function iniciarObservabilidadeEnvio(slug: string) {
  Sentry.setTags({ app: 'envio', envio_slug: slug })
}

export function marcarPassoEnvio(passo: string) {
  Sentry.setTag('envio_passo', passo)
}

/** No endereço único (slug "_") a empresa só é conhecida depois da identificação. */
export function marcarEmpresaEnvio(slug: string) {
  Sentry.setTag('envio_empresa', slug)
}

/** Em dev o envio é uma rota do app: ao sair, as tags não podem ir parar nos erros do resto do app. */
export function encerrarObservabilidadeEnvio() {
  Sentry.setTags({ app: undefined, envio_slug: undefined, envio_passo: undefined, envio_empresa: undefined })
}

/**
 * Nome da transação de carregamento: uma por rota, não por empresa (o slug está no caminho). null = não
 * é a página do envio (o nome padrão do SDK vale).
 */
export function nomeDaPaginaEnvio(loc: { hostname: string; pathname: string }): string | null {
  if (ehHostDoEnvio(loc.hostname)) return loc.pathname === '/' ? '/' : '/:slug'
  if (loc.pathname.startsWith('/enviar/')) return '/enviar/:slug'
  return null
}

/** Replay só para erro: aviso (rede do celular, Turnstile) gastaria cota e gravação no celular à toa. */
export function deveGravarReplay(evento: { level?: string }): boolean {
  return evento.level !== 'warning'
}

/** EnvioApiError pelo formato (importar a classe criaria ciclo com o cliente da API, que usa este módulo). */
function daApi(erro: unknown): { status: number; causa?: string } | null {
  return erro instanceof Error && erro.name === 'EnvioApiError' && typeof (erro as { status?: unknown }).status === 'number'
    ? (erro as unknown as { status: number; causa?: string })
    : null
}

/**
 * Recusa esperada (4xx: CPF não encontrado, sessão expirada, já enviado, muitas tentativas) é o fluxo
 * funcionando: fica só como rastro para o próximo erro. Falha do servidor (5xx) é erro; sem conexão
 * (status 0) é aviso — acontece no celular do funcionário. O resto (ex.: ler o arquivo) é erro.
 */
export function registrarFalhaEnvio(operacao: OperacaoEnvio, erro: unknown, nivel?: 'warning' | 'error') {
  const api = daApi(erro)
  if (api) {
    const { status } = api
    if (status >= 400 && status < 500) {
      Sentry.addBreadcrumb({ category: 'envio', message: `${operacao} ${status}`, level: 'info' })
      return
    }
    // 2xx sem JSON (proxy, resposta cortada): a API pode ter feito o trabalho (no /atestado, o documento
    // provavelmente foi criado) — não é "rede", é erro com agrupamento próprio.
    const invalida = api.causa === 'resposta_invalida'
    const rede = status === 0 && !invalida
    Sentry.captureException(erro, {
      level: nivel ?? (rede ? 'warning' : 'error'),
      tags: { envio_operacao: operacao, envio_status: status },
      fingerprint: ['envio', operacao, invalida ? 'resposta_invalida' : rede ? 'rede' : String(status)],
    })
    return
  }
  Sentry.captureException(erro, {
    level: nivel ?? 'error',
    tags: { envio_operacao: operacao },
    fingerprint: ['envio', operacao],
  })
}

/** Span da operação (o fetch para a API entra como filho e continua no trace da API). */
export function medirEnvio<T>(
  operacao: OperacaoEnvio,
  fn: () => Promise<T>,
  atributos?: Record<string, string | number | boolean>,
): Promise<T> {
  return Sentry.startSpan({ name: `envio ${operacao}`, op: `envio.${operacao}`, attributes: atributos }, fn)
}

/**
 * Códigos de erro do Turnstile (Cloudflare) que são CONFIGURAÇÃO — chave inválida, domínio não autorizado:
 * ninguém consegue se identificar. Os demais (desafio, navegador, rede) são de um visitante.
 */
const TURNSTILE_CONFIGURACAO = /^(110100|110110|110200|400020)$/
const turnstileJaRegistrado = new Set<string>()

/** O widget tenta de novo sozinho e repete o erro: cada código vai uma vez por página. */
export function registrarFalhaTurnstile(codigo: string | undefined) {
  const c = codigo ?? 'desconhecido'
  if (turnstileJaRegistrado.has(c)) return
  turnstileJaRegistrado.add(c)
  const configuracao = TURNSTILE_CONFIGURACAO.test(c)
  Sentry.captureException(new Error(`Turnstile: erro ${c}`), {
    level: configuracao ? 'error' : 'warning',
    tags: { envio_operacao: 'turnstile', turnstile_codigo: c },
    fingerprint: ['envio', 'turnstile', configuracao ? 'configuracao' : c.slice(0, 3)],
  })
}
